import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  changeClientPortalUserPassword,
  selectIsBusy,
  selectShowPasswordForm,
  setShowPasswordForm,
} from "./UserProfileSlice";
import { Form, Formik } from "formik";
import CPLPasswordInput from "cpl-react-common/components/Inputs/CPLPasswordInput";
import { passwordValidationRegex } from "cpl-react-common/components/Constants";

export type PasswordChangeProps = {
  currentPassword: string;
  newPassword: string;
  passwordConfirmation: string;
};

const passwordFormValidationSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Password is required"),
  newPassword: Yup.string()
    .matches(
      passwordValidationRegex,
      "Password must be at least 8 characters in length and contain one number, one upper case letter and one lower case letter"
    )
    .required("Password is required"),
  passwordConfirmation: Yup.string()
    .required("Password confirmation is required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

const UpdatePasswordForm = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const showChangePasswordForm = useSelector(selectShowPasswordForm);

  const isBusy = useSelector(selectIsBusy);

  const updatePasswordInitialValues: PasswordChangeProps = {
    currentPassword: "",
    newPassword: "",
    passwordConfirmation: "",
  };

  const updatePasswordSubmit = (values: PasswordChangeProps) => {
    thunkDispatch(changeClientPortalUserPassword(values.newPassword));
  };

  return (
    <>
      {!showChangePasswordForm ? (
        <div className="row align-items-end mb-6">
          <div className="col-12 col-md-6">
            <div className="form-label mb-1">Password</div>
            <div className="form-control form-control-plaintext">************</div>
          </div>
          <div className="col-12 col-md-6 mt-1">
            <button
              data-test-id="change-password-form__change-password-button"
              onClick={() => {
                dispatch(setShowPasswordForm(true));
              }}
              className="cpl-btn cpl-btn-primary"
            >
              Change Password
            </button>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={updatePasswordInitialValues}
          validationSchema={passwordFormValidationSchema}
          onSubmit={updatePasswordSubmit}
        >
          {({ getFieldProps }) => (
            <Form>
              <div className="row align-items-end mb-6">
                <div className="col-12 col-md-6">
                  <div className="row mb-6">
                    <label htmlFor="currentpassword" className="form-label">
                      Current Password
                    </label>
                    <CPLPasswordInput
                      data-test-id="change-password-form__current-password-input"
                      id="currentPassword"
                      className="form-control form-control-solid"
                      getFieldProps={getFieldProps}
                    />
                  </div>
                  <div className="row mb-6">
                    <label htmlFor="newpassword" className="form-label">
                      New Password
                    </label>
                    <CPLPasswordInput
                      data-test-id="change-password-form__new-password-input"
                      id="newPassword"
                      className="form-control form-control-solid"
                      getFieldProps={getFieldProps}
                    />
                    <div className="form-text">Password must be at least 8 character and contain symbols</div>
                  </div>

                  <div className="row mb-6">
                    <label htmlFor="confirmpassword" className="form-label">
                      Confirm New Password
                    </label>
                    <CPLPasswordInput
                      data-test-id="change-password-form__confirm-password-input"
                      id="passwordConfirmation"
                      className="form-control form-control-solid"
                      getFieldProps={getFieldProps}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-6 mt-1">
                  <button
                    data-test-id="change-password-form__update-password-button"
                    type="submit"
                    className="cpl-btn cpl-btn-primary me-1"
                  >
                    {!isBusy && "Update Password"}
                    {isBusy && (
                      <span className="indicator-progress" style={{ display: "block" }}>
                        Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                  <button
                    data-test-id="change-password-form__cancel-button"
                    onClick={() => {
                      dispatch(setShowPasswordForm(false));
                    }}
                    type="button"
                    className="cpl-btn cpl-btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default UpdatePasswordForm;
